import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'

import {
  ExternalSearchResult,
  Space,
  Member,
  Post,
} from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { HOTKEY_MAPPING, Hotkey } from '@tribeplatform/react-ui-kit/Hotkey'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ActionItem } from '../../../CommandBar/CommandBar.types.js'
import { dayjs } from '../../../common/lib/dayjs.js'
import { useI18n } from '../../../i18n/index.js'
import {
  ExtendedSmartSearchEntity,
  HistoryItem,
  useSearchHistory,
} from '../../index.js'
import { ExtendedSmartSearchEntityType } from '../../SmartSearch.types.js'
import { SearchResultItemPreview } from '../index.js'

interface Props {
  item: ExtendedSmartSearchEntity
  type: ExtendedSmartSearchEntityType
}

export const SearchEntityRow = ({ item, type }: Props) => {
  const { Link } = useRouter()
  const { removeItem } = useSearchHistory()
  const isPosts =
    type === ExtendedSmartSearchEntityType.POSTS ||
    type === ExtendedSmartSearchEntityType.OFFICIAL_POSTS

  const isMoreThanOneRow =
    isPosts ||
    type === ExtendedSmartSearchEntityType.EXTERNALS ||
    type === ExtendedSmartSearchEntityType.HISTORY

  const { $t } = useI18n()

  const handleRemoveFromHistory = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // Avoid the default behavior of the link to navigate to the item
    e.preventDefault()
    removeItem(item as HistoryItem)
  }

  const TrailingItem =
    type === ExtendedSmartSearchEntityType.HISTORY ? (
      <IconButton
        variant="plainNeutral"
        size="xs"
        icon={<SvgIcon name="close" />}
        accessibilityLabel={$t({
          id: 'SmartSearch.History.RemoveFromHistory',
          defaultMessage: 'Remove from history',
        })}
        className="self-center"
        onClick={handleRemoveFromHistory}
      />
    ) : (
      <Hotkey
        className={clsx('flex items-center self-stretch', {
          'h-5': !isMoreThanOneRow,
        })}
      >
        {HOTKEY_MAPPING.enter}
      </Hotkey>
    )

  const { submitItem } = useSearchHistory()

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // prevent combobox onChange, this fixes ctrl/cmd+click to open new tab
    e.stopPropagation()

    submitItem(item, type)
  }

  return (
    <Combobox.Option
      value={
        {
          item,
          entityType: type,
          url:
            (item as Post | Member | Space)?.relativeUrl ||
            (item as ExternalSearchResult)?.url ||
            (item as HistoryItem).data.url,
        } satisfies ActionItem
      }
      className={({ active }) =>
        clsx(
          'flex items-center px-3 rounded-md select-none gap-4 py-2 w-full',
          {
            'bg-surface-subdued cursor-pointer': active,
          },
        )
      }
    >
      {({ active }) => (
        <Link
          onClick={handleLinkClick}
          href={
            type === ExtendedSmartSearchEntityType.EXTERNALS ||
            type === ExtendedSmartSearchEntityType.HISTORY
              ? (item as ExternalSearchResult).url ||
                (item as HistoryItem).data.url
              : (item as Post | Member | Space)?.relativeUrl
          }
          className={clsx('flex gap-4 flex-1', {
            'min-h-12':
              type === ExtendedSmartSearchEntityType.HISTORY || isPosts,
          })}
        >
          <SearchResultItemPreview item={item} entityType={type} />
          <div className="flex-1 grid grid-cols-6 gap-1 w-full">
            <h2
              className={clsx(
                'col-span-full gap-1 font-semibold line-clamp-1',
                {
                  'lg:col-span-4': isPosts,
                },
              )}
            >
              {type === ExtendedSmartSearchEntityType.HISTORY
                ? (item as HistoryItem).data.title
                : ((item as Post).title ?? (item as Member | Space).name)}
            </h2>

            {isPosts && (
              <div
                className={clsx(
                  'col-span-2 place-self-end text-xs text-content-muted gap-1 items-center',
                )}
              >
                <span>{(item as Post).space?.name}</span>
                {(item as Post)?.space?.private && <SvgIcon name="lock" />}
              </div>
            )}

            {isMoreThanOneRow && (
              <>
                <span
                  className={clsx('text-sm line-clamp-1 col-span-full', {
                    'lg:col-span-4': isPosts,
                    'empty:hidden': !isPosts,
                  })}
                >
                  {type === ExtendedSmartSearchEntityType.HISTORY
                    ? (item as HistoryItem).data.description
                    : (item as Post | ExternalSearchResult).description}
                </span>

                {isPosts && (
                  <span
                    className={clsx(
                      'col-span-2 place-self-end text-xs text-content-muted gap-1 items-center',
                    )}
                  >
                    {dayjs((item as Post).publishedAt).fromNow()}
                  </span>
                )}
              </>
            )}
          </div>
          {active && TrailingItem}
        </Link>
      )}
    </Combobox.Option>
  )
}
